import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion as m } from "framer-motion";
import PageHeader from "../components/PageHeader";
import {
  Button,
  Marquee,
  Reveal,
  Section,
  SectionHeading,
  Tag,
} from "../components/ui";
import { services, toolkit } from "../data/site";

const Skills = () => {
  const [open, setOpen] = useState(0);

  return (
    <>
      <Helmet>
        <title>Capabilities — Jephthah Boateng</title>
        <meta
          name="description"
          content="Backend and API engineering, data pipelines, frontend architecture and platform work — what I do, and the tools I do it with."
        />
      </Helmet>

      <PageHeader
        eyebrow="Capabilities"
        title="What I actually do all day."
        lead="Four practices that share one method: find the real constraint, make the decision legible, and build the thing that survives contact with reality."
        meta={["4 practices", "30+ tools", "Design to deployment"]}
      />

      {/* Services accordion */}
      <Section tone="default">
        <SectionHeading
          eyebrow="Practices"
          index={1}
          title="Four disciplines, closely related."
        />

        <div className="mt-14">
          {services.map((service, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={service.title} delay={i * 0.05}>
                <div className="border-t border-[var(--color-border)]">
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="group flex w-full items-baseline gap-5 py-8 text-left md:gap-10"
                  >
                    <span className="font-mono text-[11px] tracking-[0.2em] text-electric">
                      {String(service.index).padStart(2, "0")}
                    </span>
                    <span
                      className={`flex-1 font-display text-3xl leading-tight transition-colors duration-300 md:text-[2.5rem] ${
                        isOpen
                          ? "text-azure dark:text-electric"
                          : "group-hover:text-azure dark:group-hover:text-electric"
                      }`}
                    >
                      {service.title}
                    </span>
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] transition-transform duration-500 ease-emphasis ${
                        isOpen ? "rotate-45 border-azure dark:border-electric" : ""
                      }`}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <m.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-8 pb-10 md:grid-cols-12 md:pl-[3.75rem]">
                          <div className="md:col-span-7">
                            <p className="t-lead">{service.summary}</p>
                            <p className="mt-5 max-w-prose text-[15px] leading-relaxed text-ink-muted dark:text-slightwhite/70">
                              {service.detail}
                            </p>
                          </div>
                          <div className="md:col-span-5">
                            <p className="t-eyebrow">Includes</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {service.capabilities.map((c) => (
                                <Tag key={c} variant="azure">
                                  {c}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        </div>
                      </m.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
          <div className="rule" />
        </div>
      </Section>

      <Marquee
        items={["Python", "TypeScript", "FastAPI", "React", "PostgreSQL", "AWS"]}
        className="border-y border-[var(--color-border)] bg-paper-warm py-6 dark:bg-night-surface"
        itemClassName="font-display text-3xl md:text-5xl"
        reverse
      />

      {/* Toolkit */}
      <Section tone="default">
        <SectionHeading
          eyebrow="Toolkit"
          index={2}
          title="The tools, honestly listed."
          lead="Everything here is something I have shipped with — not something I read about once."
        />

        <div className="mt-14 space-y-0">
          {toolkit.map((group, i) => (
            <Reveal key={group.group} delay={i * 0.05}>
              <div className="grid gap-5 border-t border-[var(--color-border)] py-8 md:grid-cols-12 md:gap-10">
                <p className="t-eyebrow md:col-span-3">{group.group}</p>
                <div className="flex flex-wrap gap-x-7 gap-y-3 md:col-span-9">
                  {group.items.map((item) => (
                    <span
                      key={item}
                      className="font-display text-xl transition-colors duration-300 hover:text-azure dark:hover:text-electric md:text-2xl"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
          <div className="rule" />
        </div>
      </Section>

      {/* Process */}
      <section className="relative overflow-hidden bg-gradient-brand py-24 text-paper md:py-32">
        <div className="noise-panel pointer-events-none absolute inset-0 opacity-[0.08]" />
        <div className="relative mx-auto w-full max-w-content px-[var(--gutter)]">
          <span className="t-eyebrow text-electric">
            <span className="mr-3 inline-block h-px w-8 bg-electric align-middle" />
            03 — Process
          </span>
          <h2 className="t-title mt-7 max-w-3xl">
            How a project actually runs.
          </h2>

          <div className="mt-16 grid gap-10 md:grid-cols-4">
            {[
              {
                step: "Define",
                body: "We establish what is genuinely broken and what success would look like in numbers, not adjectives.",
              },
              {
                step: "Design",
                body: "Flows and interfaces at the fidelity the decision requires. Rejected options are documented, not hidden.",
              },
              {
                step: "Build",
                body: "Typed, accessible, performance-budgeted. Shipped in slices so feedback arrives before the end.",
              },
              {
                step: "Hand over",
                body: "Documentation, a component inventory and a system your team can extend without me.",
              },
            ].map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-7">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-electric">
                    0{i + 1}
                  </span>
                  <h3 className="mt-3 font-display text-2xl">{p.step}</h3>
                  <p className="mt-3 text-sm leading-[1.8] text-paper/70">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14">
            <Button to="/contact" variant="electric" size="lg" withArrow>
              Start a project
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Skills;
